import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SiteContextService } from '../../core/site-context.service';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { ConfirmationDialogData } from '../confirmation-dialog/confirmation-dialog-config';

// Shared by every RouterLink that can point at a campaign on another account: a plain RouterLink only reaches campaigns on the currently active account, so those fall back to this confirm-then-switch flow instead.
@Injectable({ providedIn: 'root' })
export class CrossAccountNavigationService {
  private siteContextService = inject(SiteContextService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private translate = inject(TranslateService);

  public isOtherAccount(siteId: number | undefined): boolean {
    return !!siteId && siteId !== this.siteContextService.getCurrentSiteId();
  }

  private isModifiedClick(event?: MouseEvent): boolean {
    return !!event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1);
  }

  // Bind straight from a template alongside [routerLink]="crossAccountNavService.isOtherAccount(siteId) ? null : someLink": a same-account click is a no-op (RouterLink already owns it), otherwise this confirms, switches the account and hard-navigates to `url`.
  public handleLinkClick(event: MouseEvent, siteId: number | undefined, url: string, accountName?: string): void {
    if (!this.isOtherAccount(siteId)) {
      return;
    }
    this.navigate(siteId, url, event, accountName);
  }

  // Call from a (click) handler on a link whose RouterLink is bound to null for cross-account rows; confirms, then switches the account and hard-navigates to `url`.
  public navigate(targetSiteId: number, url: string, event?: MouseEvent, accountName?: string): void {
    if (this.isModifiedClick(event)) {
      return;
    }
    event?.preventDefault();
    const name = accountName || this.translate.instant('customer-dashboard.campaign-table.unknown-account');
    const dialogData = new ConfirmationDialogData(
      this.translate.instant('customer-dashboard.campaign-table.messages.switch-account-title'),
      this.translate.instant('customer-dashboard.campaign-table.messages.switch-account-message', {
        accountName: name,
      }),
      undefined,
      undefined,
      undefined,
      'Yes',
      'Cancel',
    );

    this.confirmationDialog.confirm(dialogData).subscribe((confirmed) => {
      if (confirmed) {
        this.siteContextService.updateCurrentSiteId(targetSiteId);
        window.location.href = url;
      }
    });
  }
}
